import { chartBarStartSec } from '@/lib/chart-timeframe'
import {
  type ChartSeriesSource,
  type ChartSeriesSourceId,
  getSeriesKey,
  type SeriesFetchContext,
  type SeriesKeyContext,
  type SeriesPoint,
  type SeriesSourceRegistry,
} from '@/lib/tradingview/datafeeds/series-sources'
import type { ChartAnalyticsMetric } from '@/types/dashboard'

// Grace before teardown after the last release — quick toggles and the
// remove+add churn of symbol/timeframe changes must not refetch. Parity with
// state/market-data-cache.ts CLEANUP_GRACE_MS.
const SERIES_CLEANUP_GRACE_MS = 10_000
// Gap backfills push several ascending bars through pushBars in one burst;
// coalesce to a single trailing-window refresh.
const SERIES_REFRESH_DEBOUNCE_MS = 250
// Trailing buckets re-fetched on each bar close (includes the just-closed one).
const REFRESH_TRAILING_BARS = 3
// Caps live/trailing-refresh growth per series (see storeSeriesPoints' forward-
// move gate) — a backward-pan backfill (extendActiveWindows) never trims, so
// this only bounds how much recent trickle accumulates before the oldest of
// it is dropped.
const MAX_SERIES_CACHE_BUCKETS = 500

type SeriesUpdatedCallback = (
  sourceId: ChartSeriesSourceId,
  metrics: ChartAnalyticsMetric[],
  // True only on the empty→has-data transition (first history landing for
  // this key) — TV's own custom-study main() already ran once, empty, for
  // every historical bar before this async fetch could possibly resolve.
  isFirstData: boolean
) => void

type ActiveSeriesEntry = {
  source: ChartSeriesSource
  ctx: SeriesFetchContext
  abortController: AbortController | null
  unsubscribeLive: (() => void) | null
  inFlight: boolean
  hasData: boolean
  warnedFetchFailure: boolean
  // Latest bucket time this entry has ever stored, used only to tell a
  // forward-moving write (a new live/trailing-refresh point) from a
  // backward-pan write (extendActiveWindows backfilling older history) — see
  // storeSeriesPoints. Mirrors chart-datafeed.ts's groupLatestTime.
  latestKnownTimeSec: number | undefined
}

type ManagerOptions = {
  graceMs?: number
  refreshDebounceMs?: number
  now?: () => number
}

/**
 * Refcounted own-fetch series subscriptions for TV studies. Held by
 * ChartDatafeed; closed to modification, open to extension via the
 * SeriesSourceRegistry (see series-sources.ts).
 */
export class SeriesSubscriptionManager {
  private readonly registry: SeriesSourceRegistry
  private readonly graceMs: number
  private readonly refreshDebounceMs: number
  private readonly now: () => number
  private onSeriesUpdated: SeriesUpdatedCallback | null = null

  private readonly refCounts = new Map<string, number>()
  private readonly cleanupTimers = new Map<string, ReturnType<typeof setTimeout>>()
  private readonly entries = new Map<string, ActiveSeriesEntry>()
  /** key → metric → bucketTimeSec → value. In-memory only, never persisted. */
  private readonly seriesByKey = new Map<string, Map<ChartAnalyticsMetric, Map<number, number>>>()
  private refreshTimer: ReturnType<typeof setTimeout> | null = null

  constructor(registry: SeriesSourceRegistry, options?: ManagerOptions) {
    this.registry = registry
    this.graceMs = options?.graceMs ?? SERIES_CLEANUP_GRACE_MS
    this.refreshDebounceMs = options?.refreshDebounceMs ?? SERIES_REFRESH_DEBOUNCE_MS
    this.now = options?.now ?? Date.now
  }

  setOnSeriesUpdated(callback: SeriesUpdatedCallback | null): void {
    this.onSeriesUpdated = callback
  }

  hasMetric(metric: ChartAnalyticsMetric): boolean {
    return this.registry.hasMetric(metric)
  }

  refCountFor(sourceId: ChartSeriesSourceId, ctx: SeriesKeyContext): number {
    return this.refCounts.get(getSeriesKey(sourceId, ctx)) ?? 0
  }

  acquire(sourceId: ChartSeriesSourceId, ctx: SeriesFetchContext): void {
    const source = this.registry.getById(sourceId)
    if (!source) return
    const key = getSeriesKey(sourceId, ctx)

    const pending = this.cleanupTimers.get(key)
    if (pending) {
      clearTimeout(pending)
      this.cleanupTimers.delete(key)
    }

    const next = (this.refCounts.get(key) ?? 0) + 1
    this.refCounts.set(key, next)
    if (next > 1) return
    // Re-acquired within the grace window: the entry + cache are still warm.
    if (this.entries.has(key)) return

    const entry: ActiveSeriesEntry = {
      source,
      ctx,
      abortController: null,
      unsubscribeLive: null,
      inFlight: false,
      hasData: false,
      warnedFetchFailure: false,
      latestKnownTimeSec: undefined,
    }
    this.entries.set(key, entry)

    if (this.isBelowMinTimeframe(entry)) return

    if (source.refresh === 'stream' && source.subscribeLive) {
      entry.unsubscribeLive = source.subscribeLive(ctx, (points) => {
        const hadDataBefore = entry.hasData
        this.storeSeriesPoints(entry, key, ctx.timeframeSeconds, points)
        entry.hasData = true
        this.onSeriesUpdated?.(source.id, source.metrics, !hadDataBefore)
      })
    }

    void this.backfill(key, entry)
  }

  release(sourceId: ChartSeriesSourceId, ctx: SeriesFetchContext): void {
    const key = getSeriesKey(sourceId, ctx)
    const current = this.refCounts.get(key)
    if (current === undefined) return
    const next = current - 1
    if (next > 0) {
      this.refCounts.set(key, next)
      return
    }
    this.refCounts.delete(key)
    const timer = setTimeout(() => {
      this.cleanupTimers.delete(key)
      this.evict(key)
    }, this.graceMs)
    this.cleanupTimers.set(key, timer)
  }

  /** Debounced bar-close hook: refresh every active 'bar-close' key. Fired by
   *  the realtime bridge (one call per closed bar; bursts coalesce). */
  notifyBarClose(): void {
    if (this.refreshTimer) return
    this.refreshTimer = setTimeout(() => {
      this.refreshTimer = null
      this.refreshActiveKeys()
    }, this.refreshDebounceMs)
  }

  /** Converge refcounts to the desired multiset (one entry per active
   *  own-fetch study instance). Extra refs are released (grace applies),
   *  missing refs are acquired. */
  syncActiveStudies(
    desired: Array<{ sourceId: ChartSeriesSourceId; ctx: SeriesFetchContext }>
  ): void {
    const desiredByKey = new Map<
      string,
      { sourceId: ChartSeriesSourceId; ctx: SeriesFetchContext; count: number }
    >()
    for (const { sourceId, ctx } of desired) {
      if (!this.registry.getById(sourceId)) continue
      const key = getSeriesKey(sourceId, ctx)
      const existing = desiredByKey.get(key)
      if (existing) existing.count += 1
      else desiredByKey.set(key, { sourceId, ctx, count: 1 })
    }

    for (const [key, current] of [...this.refCounts]) {
      const want = desiredByKey.get(key)?.count ?? 0
      const entry = this.entries.get(key)
      if (!entry) continue
      for (let i = current; i > want; i--) this.release(entry.source.id, entry.ctx)
    }

    for (const { sourceId, ctx, count } of desiredByKey.values()) {
      const key = getSeriesKey(sourceId, ctx)
      const current = this.refCounts.get(key) ?? 0
      for (let i = current; i < count; i++) this.acquire(sourceId, ctx)
    }
  }

  /** Closes the "no left-pan backfill" v1 gap: syncActiveStudies only reacts
   *  to study add/remove, never to panning, so an already-active series' window
   *  was fixed at acquire time forever. Call this on visible-range changes —
   *  for each already-active series whose newly-desired fromSec reaches
   *  further back than what's cached, backfill the gap and remember the
   *  extended boundary. Series that aren't active yet are untouched; acquire()
   *  seeds those. */
  extendActiveWindows(
    desired: Array<{ sourceId: ChartSeriesSourceId; ctx: SeriesFetchContext }>
  ): void {
    const seen = new Set<string>()
    for (const { sourceId, ctx } of desired) {
      const key = getSeriesKey(sourceId, ctx)
      if (seen.has(key)) continue
      seen.add(key)

      const entry = this.entries.get(key)
      if (!entry) continue
      if (this.isBelowMinTimeframe(entry)) continue
      if (entry.inFlight) continue // retried on the next visible-range change once this settles
      if (ctx.fromSec >= entry.ctx.fromSec) continue

      const gap = { fromSec: ctx.fromSec, toSec: entry.ctx.fromSec }
      entry.ctx = { ...entry.ctx, fromSec: ctx.fromSec }
      void this.backfill(key, entry, gap)
    }
  }

  getValue(metric: ChartAnalyticsMetric, timeSec: number, ctx: SeriesKeyContext): number | null {
    const source = this.registry.getByMetric(metric)
    if (!source) return null
    const key = getSeriesKey(source.id, ctx)
    const byTime = this.seriesByKey.get(key)?.get(metric)
    if (!byTime) return null

    const bucketSec = chartBarStartSec(timeSec, ctx.timeframeSeconds)
    const exact = byTime.get(bucketSec)
    if (exact !== undefined) return exact
    if (!source.carryForward) return null

    // Linear scan (O(buckets)) — a nonce-driven full recompute reads every
    // loaded bar, so this is O(bars×buckets) per sparse study per bar close.
    // Fine at current scale (≤500 buckets, a handful of sparse studies); swap
    // in a sorted index if it ever shows up in a profile.
    let best: number | null = null
    let bestTime = -Infinity
    for (const [time, value] of byTime) {
      if (time <= bucketSec && time > bestTime) {
        bestTime = time
        best = value
      }
    }
    return best
  }

  destroy(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer)
      this.refreshTimer = null
    }
    for (const timer of this.cleanupTimers.values()) clearTimeout(timer)
    this.cleanupTimers.clear()
    for (const key of [...this.entries.keys()]) this.evict(key)
    this.refCounts.clear()
  }

  private isBelowMinTimeframe(entry: ActiveSeriesEntry): boolean {
    return (
      entry.source.minTimeframeSeconds !== undefined &&
      entry.ctx.timeframeSeconds < entry.source.minTimeframeSeconds
    )
  }

  private refreshActiveKeys(): void {
    const nowSec = Math.floor(this.now() / 1000)
    for (const [key, entry] of this.entries) {
      if (!this.refCounts.has(key)) continue // draining in grace
      if (entry.inFlight) continue // don't abort a fetch that is still warming the cache
      if (this.isBelowMinTimeframe(entry)) continue
      if (entry.source.refresh === 'stream' && entry.unsubscribeLive) continue // live-fed
      // hasData → trailing window, widened back to the newest cached bucket so
      // a realtime gap (sleep/reconnect) is refetched instead of left as a
      // permanent NaN hole; empty (initial fetch failed) → retry the full
      // window. Never fetches older than entry.ctx.fromSec — extendActiveWindows
      // owns backfilling further back on pan and keeps that boundary current.
      const window = entry.hasData
        ? { fromSec: this.refreshFromSec(key, entry, nowSec), toSec: nowSec }
        : undefined
      void this.backfill(key, entry, window)
    }
  }

  /** Trailing refresh covers only the last REFRESH_TRAILING_BARS buckets, but
   *  if the newest cached bucket predates that window (a realtime gap), widen
   *  fromSec back to it so the gap is healed. Never fetch older than the
   *  acquire-time window. */
  private refreshFromSec(key: string, entry: ActiveSeriesEntry, nowSec: number): number {
    const trailingFrom = nowSec - REFRESH_TRAILING_BARS * Math.max(1, entry.ctx.timeframeSeconds)
    const newest = this.newestCachedBucket(key)
    if (newest === null) return trailingFrom
    return Math.max(entry.ctx.fromSec, Math.min(trailingFrom, newest))
  }

  private newestCachedBucket(key: string): number | null {
    const byMetric = this.seriesByKey.get(key)
    if (!byMetric) return null
    let newest: number | null = null
    for (const byTime of byMetric.values()) {
      for (const time of byTime.keys()) {
        if (newest === null || time > newest) newest = time
      }
    }
    return newest
  }

  private async backfill(
    key: string,
    entry: ActiveSeriesEntry,
    window?: { fromSec: number; toSec: number }
  ): Promise<void> {
    entry.abortController?.abort()
    const controller = new AbortController()
    entry.abortController = controller
    entry.inFlight = true
    const ctx = window ? { ...entry.ctx, ...window } : entry.ctx
    try {
      const points = await entry.source.fetchHistory(ctx, controller.signal)
      if (controller.signal.aborted) return
      const hadDataBefore = entry.hasData
      this.storeSeriesPoints(entry, key, entry.ctx.timeframeSeconds, points)
      entry.hasData = this.hasCachedData(key)
      this.onSeriesUpdated?.(entry.source.id, entry.source.metrics, !hadDataBefore && entry.hasData)
    } catch (error) {
      if (controller.signal.aborted) return
      if (!entry.warnedFetchFailure) {
        entry.warnedFetchFailure = true
        console.warn(`[SeriesSubscriptionManager] fetchHistory failed for ${key}:`, error)
      }
    } finally {
      entry.inFlight = false
    }
  }

  private storeSeriesPoints(
    entry: ActiveSeriesEntry,
    key: string,
    timeframeSeconds: number,
    points: SeriesPoint[]
  ): void {
    if (points.length === 0) return
    let byMetric = this.seriesByKey.get(key)
    if (!byMetric) {
      byMetric = new Map()
      this.seriesByKey.set(key, byMetric)
    }
    let maxBucketSec: number | undefined
    for (const point of points) {
      // Drop non-positive timestamps: a proto3 int64 default of '0' decodes to
      // epoch 0, and with carryForward it would paint the stale value across
      // every bar older than the first real point.
      if (!Number.isFinite(point.timeSec) || point.timeSec <= 0) continue
      const bucketSec = chartBarStartSec(point.timeSec, timeframeSeconds)
      if (maxBucketSec === undefined || bucketSec > maxBucketSec) maxBucketSec = bucketSec
      for (const [metric, value] of Object.entries(point.values) as Array<
        [ChartAnalyticsMetric, number]
      >) {
        if (typeof value !== 'number' || !Number.isFinite(value)) continue
        let byTime = byMetric.get(metric)
        if (!byTime) {
          byTime = new Map<number, number>()
          byMetric.set(metric, byTime)
        }
        byTime.set(bucketSec, value)
      }
    }

    if (maxBucketSec === undefined) return
    // A backward-pan backfill (extendActiveWindows) must never trim — it would
    // delete the older bars this write just fetched, reproducing the original
    // "no left-pan backfill" bug one layer down. Only a write that extends the
    // known range forward is eligible.
    const isForwardMove =
      entry.latestKnownTimeSec === undefined || maxBucketSec > entry.latestKnownTimeSec
    if (isForwardMove) {
      entry.latestKnownTimeSec = maxBucketSec
      this.pruneKey(byMetric)
    }
  }

  private pruneKey(byMetric: Map<ChartAnalyticsMetric, Map<number, number>>): void {
    for (const byTime of byMetric.values()) {
      if (byTime.size <= MAX_SERIES_CACHE_BUCKETS) continue
      const times = [...byTime.keys()].sort((a, b) => a - b)
      for (const time of times.slice(0, times.length - MAX_SERIES_CACHE_BUCKETS)) {
        byTime.delete(time)
      }
    }
  }

  private hasCachedData(key: string): boolean {
    const byMetric = this.seriesByKey.get(key)
    if (!byMetric) return false
    for (const byTime of byMetric.values()) {
      if (byTime.size > 0) return true
    }
    return false
  }

  private evict(key: string): void {
    const entry = this.entries.get(key)
    entry?.abortController?.abort()
    entry?.unsubscribeLive?.()
    this.entries.delete(key)
    this.seriesByKey.delete(key)
  }
}
